import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LibraryModule } from '../shared/lib/lib.module';
import {
  SeniorCenterApplicationService,
  SeniorCenterApplicationDetailItem,
} from '../core/services/senior-center-application.service';
import { FormApiService } from '../core/services/formapiService';
import { FinancialYearFilterService } from '../core/services/financial-year-filter.service';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, skip } from 'rxjs/operators';
import { FormDateUtils } from '../shared/utils/form-date-utils';

export interface ApplicationAttachmentItem {
  id: string;
  label: string;
  category: string;
  blobName: string;
  fileName: string;
  isLoading: boolean;
  error: string | null;
}

@Component({
  selector: 'senior-center-application-detail-component, SeniorCenterApplicationDetailComponent',
  standalone: true,
  templateUrl: './senior-center-application-detail.component.html',
  styleUrls: ['./senior-center-application-detail.component.css'],
  imports: [CommonModule, FormsModule, LibraryModule, RouterModule],
})
export class SeniorCenterApplicationDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly seniorCenterService = inject(SeniorCenterApplicationService);
  private readonly formApiService = inject(FormApiService);
  private readonly financialYearFilterService = inject(FinancialYearFilterService);
  private routeSubscription?: Subscription;
  private financialYearSubscription?: Subscription;
  private currentApplicationId: string | null = null;

  isLoading = true;
  isSubmitting = false;
  errorMessage: string | null = null;
  actionError: string | null = null;
  successMessage: string | null = null;

  application: SeniorCenterApplicationDetailItem | null = null;
  attachments: ApplicationAttachmentItem[] = [];
  isLoadingAttachments = false;
  attachmentsError: string | null = null;

  showRejectForm = false;
  showHardRejectForm = false;
  rejectComment = '';

  showApproveModal = false;
  selectedEligibility: string | null = null;

  readonly eligibilityOptions = [
    {
      id: 'spouse_under_60',
      label: 'Eligible person is under 60 and the spouse of person 60 or older who takes part in the nutrition program.',
    },
    {
      id: 'volunteer_under_60',
      label: 'Eligible person is under 60, serves as volunteer at the nutrition site and the provider offers a meal according to AAA procedures.',
    },
    {
      id: 'disability_housing_facility',
      label: 'Eligible person is under 60, has a disability and lives in a housing facility occupied primarily by people 60 and over where congregate meals are served.',
    },
    {
      id: 'disability_lives_with_eligible',
      label: 'Eligible person is under 60, has a disability, lives with a person eligible for a meal and the provider offers a meal according to AAA procedures.',
    },
  ];

  get eligibilityRadioList(): { id: string; value: string; label: string }[] {
    return this.eligibilityOptions.map((option) => ({ id: option.id, value: option.id, label: option.label }));
  }

  ngOnInit(): void {
    this.routeSubscription = this.route.paramMap.subscribe((params) => {
      const applicationId = params.get('applicationId');
      if (!applicationId) {
        this.router.navigate(['/senior-center-applications']);
        return;
      }
      this.currentApplicationId = applicationId;
      this.fetchDetail(applicationId);
    });

    this.financialYearSubscription = this.financialYearFilterService.selectedYear$
      .pipe(skip(1))
      .subscribe(() => {
        if (this.currentApplicationId) {
          this.fetchDetail(this.currentApplicationId);
        }
      });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    this.financialYearSubscription?.unsubscribe();
  }

  private fetchDetail(applicationId: string): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.actionError = null;
    this.successMessage = null;
    this.showRejectForm = false;
    this.rejectComment = '';
    this.showApproveModal = false;
    this.selectedEligibility = null;
    this.attachments = [];
    this.attachmentsError = null;
    this.isLoadingAttachments = true;

    forkJoin({
      detail: this.seniorCenterService.getApplicationDetail(applicationId),
      form: this.seniorCenterService.getApplicationForm(applicationId).pipe(
        catchError(() => of(null))
      ),
    }).subscribe({
      next: ({ detail, form }) => {
        this.application = detail.application ?? null;
        this.attachments = this.extractAttachmentsFromFormJson(form?.formJson ?? null);
        this.isLoading = false;
        this.isLoadingAttachments = false;
      },
      error: (err) => {
        this.errorMessage = err?.error?.message ?? 'Unable to load application details.';
        this.isLoading = false;
        this.isLoadingAttachments = false;
      },
    });
  }

  private extractAttachmentsFromFormJson(formJson: Record<string, unknown> | null): ApplicationAttachmentItem[] {
    if (!formJson || typeof formJson !== 'object') {
      return [];
    }

    const items: ApplicationAttachmentItem[] = [];
    const seen = new Set<string>();

    const pushKeys = (keys: string[], category: string, label: string): void => {
      keys.forEach((blobName, index) => {
        const key = blobName.trim();
        if (!key || seen.has(key)) {
          return;
        }
        seen.add(key);
        const fallbackName = key.includes('/') ? key.split('/').pop() || key : key;
        items.push({
          id: `${category}-${items.length}-${index}`,
          label: keys.length > 1 ? `${label} (${index + 1})` : label,
          category,
          blobName: key,
          fileName: fallbackName,
          isLoading: false,
          error: null,
        });
      });
    };

    const clientRights = (formJson['client_rights_responsibilities'] ?? {}) as Record<string, unknown>;
    pushKeys(
      this.normalizeBlobKeys(
        clientRights['identification_proof_document'] ??
          clientRights['identification_proof_upload_key'] ??
          formJson['client_rights_identification_proof_file']
      ),
      'Identification Proof',
      'Identification document'
    );

    const signedAgreements = (formJson['signed_agreements'] ?? {}) as Record<string, unknown>;
    pushKeys(
      this.normalizeBlobKeys(
        signedAgreements['documents'] ?? formJson['signed_agreements_files']
      ),
      'Signed Agreements',
      'Signed agreement document'
    );

    const topLevelPlans = Array.isArray(formJson['health_plans']) ? formJson['health_plans'] : [];
    topLevelPlans.forEach((plan: any, planIndex: number) => {
      const recipientHint = String(plan?.recipient_name ?? plan?.recipient_email ?? '').trim();
      const label = recipientHint
        ? `Health plan attachment — ${recipientHint}`
        : `Health plan attachment ${planIndex + 1}`;
      pushKeys(this.normalizeBlobKeys(plan?.upload_key ?? plan?.documents), 'Health Plan', label);
    });

    const recipients = Array.isArray(formJson['recipients']) ? formJson['recipients'] : [];
    recipients.forEach((recipient: any, recipientIndex: number) => {
      const recipientName =
        `${recipient?.first_name ?? recipient?.recipient_first_name ?? ''} ${
          recipient?.last_name ?? recipient?.recipient_last_name ?? ''
        }`.trim() || `Recipient ${recipientIndex + 1}`;
      const plans = Array.isArray(recipient?.health_plans) ? recipient.health_plans : [];
      plans.forEach((plan: any, planIndex: number) => {
        pushKeys(
          this.normalizeBlobKeys(plan?.upload_key ?? plan?.documents),
          'Health Plan',
          `Health plan attachment — ${recipientName}${plans.length > 1 ? ` (${planIndex + 1})` : ''}`
        );
      });
    });

    return items;
  }

  private normalizeBlobKeys(value: unknown): string[] {
    if (value == null || value === '') {
      return [];
    }
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === 'string') {
            return item.trim();
          }
          if (item && typeof item === 'object') {
            const record = item as Record<string, unknown>;
            return String(
              record['blobName'] ??
                record['blob_name'] ??
                record['key'] ??
                record['Key'] ??
                record['id'] ??
                ''
            ).trim();
          }
          return '';
        })
        .filter(Boolean);
    }
    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const nested =
        record['keys'] ??
        record['Keys'] ??
        record['documents'] ??
        record['Documents'] ??
        record['upload_key'];
      if (nested != null) {
        return this.normalizeBlobKeys(nested);
      }
      const single = record['blobName'] ?? record['blob_name'] ?? record['key'] ?? record['Key'];
      return single != null && String(single).trim() ? [String(single).trim()] : [];
    }

    const raw = String(value).trim();
    if (!raw) {
      return [];
    }
    if ((raw.startsWith('[') && raw.endsWith(']')) || (raw.startsWith('{') && raw.endsWith('}'))) {
      try {
        return this.normalizeBlobKeys(JSON.parse(raw));
      } catch {
        /* fall through */
      }
    }
    return raw
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
  }

  viewAttachment(item: ApplicationAttachmentItem): void {
    this.loadAttachmentBlob(item, (blob, filename) => {
      const url = URL.createObjectURL(blob);
      const opened = window.open(url, '_blank', 'noopener,noreferrer');
      if (!opened) {
        this.triggerBrowserDownload(blob, filename);
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    });
  }

  downloadAttachment(item: ApplicationAttachmentItem): void {
    this.loadAttachmentBlob(item, (blob, filename) => {
      this.triggerBrowserDownload(blob, filename);
    });
  }

  private loadAttachmentBlob(
    item: ApplicationAttachmentItem,
    onSuccess: (blob: Blob, filename: string) => void
  ): void {
    if (!item?.blobName || item.isLoading) {
      return;
    }
    item.isLoading = true;
    item.error = null;
    this.attachmentsError = null;

    const encodedPath = item.blobName
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');

    this.formApiService.getFileWithHeaders(encodedPath).subscribe({
      next: ({ blob, filename }) => {
        item.isLoading = false;
        item.fileName = filename || item.fileName;
        onSuccess(blob, item.fileName);
      },
      error: () => {
        item.isLoading = false;
        item.error = 'Unable to open this document.';
        this.attachmentsError = 'One or more documents could not be loaded.';
      },
    });
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename || 'document';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  formatSubmittedDate(value: string | null | undefined): string {
    if (!value) return '-';
    const date = FormDateUtils.parseDateOnly(value);
    if (!date) return '-';
    return FormDateUtils.formatDate(date, 'MM/DD/YYYY');
  }

  formatApprovedDate(application: SeniorCenterApplicationDetailItem | null): string {
    if (!application) return '-';
    const status = String(application.status ?? '').trim().toLowerCase();
    const isApproved = status === 'approved' || status === 'approve';
    const raw =
      application.approvedDate ?? (isApproved ? application.updatedAt : null);
    if (!raw) return '-';
    const date = FormDateUtils.parseDateOnly(raw);
    if (!date) return '-';
    return FormDateUtils.formatDate(date, 'MM/DD/YYYY');
  }

  formatLivesAlone(value: string | null | undefined): string {
    const v = (value ?? '').toString().trim().toLowerCase();
    if (!v) return '-';
    if (v === 'yes' || v === 'true') return 'Yes';
    if (v === 'no' || v === 'false') return 'No';
    return value || '-';
  }

  formatStatus(status: string | null | undefined): string {
    if (!status) return '-';
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  get isReturned(): boolean {
    return (this.application?.status ?? '').toLowerCase() === 'returned';
  }

  get isRejected(): boolean {
    return (this.application?.status ?? '').toLowerCase() === 'rejected';
  }

  get isApproved(): boolean {
    return (this.application?.status ?? '').toLowerCase() === 'approved';
  }

  get canReview(): boolean {
    const status = (this.application?.status ?? '').toLowerCase();
    return status !== 'approved' && status !== 'rejected' && status !== 'returned';
  }

  /** Rejected/returned apps keep form data — staff can reopen as draft and continue editing. */
  get canContinueApplication(): boolean {
    const status = (this.application?.status ?? '').toLowerCase().trim();
    return (
      status === 'rejected' ||
      status === 'returned' ||
      status === 'denied' ||
      status === 'draft'
    );
  }

  onContinueApplication(): void {
    const id = this.application?.applicationId?.trim();
    if (!id || this.isSubmitting) {
      return;
    }

    const status = (this.application?.status ?? '').toLowerCase().trim();
    const needsReopen =
      status === 'rejected' || status === 'returned' || status === 'denied';

    const navigateToEnrollment = (): void => {
      sessionStorage.setItem('programName', 'Senior Center');
      sessionStorage.setItem('staff_enrollment_mode', 'true');
      sessionStorage.setItem('program_status', 'draft');
      void this.router.navigate(['/program', '1000'], {
        queryParams: {
          staffEnrollment: 'true',
          applicationId: id,
          resumeDraft: 'true',
        },
      });
    };

    if (!needsReopen) {
      navigateToEnrollment();
      return;
    }

    this.isSubmitting = true;
    this.actionError = null;
    this.seniorCenterService.reopenApplicationAsDraft(id).subscribe({
      next: () => {
        this.isSubmitting = false;
        navigateToEnrollment();
      },
      error: (err) => {
        this.isSubmitting = false;
        this.actionError =
          err?.error?.message ??
          'Unable to reopen application for editing. Please try again.';
      },
    });
  }

  /**
   * Nutrition under-60 eligibility screen is only for applicants under 60
   * (e.g. spouse exception). Age 60+ skips it. Missing age → show screen (safe).
   */
  get requiresNutritionEligibility(): boolean {
    const age = this.application?.applicantAge;
    if (typeof age === 'number' && Number.isFinite(age)) {
      return age < 60;
    }
    return true;
  }

  onPrevious(): void {
    const prevId = this.application?.previousApplicationId;
    if (prevId) {
      this.router.navigate(['/senior-center-applications', prevId]);
    }
  }

  onNext(): void {
    const nextId = this.application?.nextApplicationId;
    if (nextId) {
      this.router.navigate(['/senior-center-applications', nextId]);
    }
  }

  onOpenApproveModal(): void {
    if (!this.application?.applicationId || this.isSubmitting) return;
    this.selectedEligibility = null;
    this.actionError = null;
    this.successMessage = null;

    if (!this.requiresNutritionEligibility) {
      this.confirmApprove();
      return;
    }

    this.showApproveModal = true;
  }

  onCloseApproveModal(): void {
    this.showApproveModal = false;
    this.selectedEligibility = null;
    this.actionError = null;
  }

  onEligibilityChange(selected: string | null): void {
    this.selectedEligibility = selected;
  }

  private getEligibilityLabel(selectedId: string | null): string {
    const option = this.eligibilityOptions.find((item) => item.id === selectedId);
    return option?.label ?? selectedId ?? '';
  }

  confirmApprove(): void {
    if (!this.application?.applicationId || this.isSubmitting) return;

    if (this.requiresNutritionEligibility && !this.selectedEligibility) {
      this.actionError = 'Please select an eligibility option before approving.';
      return;
    }

    this.isSubmitting = true;
    this.actionError = null;
    this.successMessage = null;

    const eligibilityLabel = this.selectedEligibility
      ? this.getEligibilityLabel(this.selectedEligibility)
      : null;
    this.seniorCenterService
      .reviewApplication(this.application.applicationId, {
        status: 'approved',
        eligibilityCategories: eligibilityLabel ? [eligibilityLabel] : undefined,
      })
      .subscribe({
        next: (res) => {
          this.successMessage = res.rfidNumber
            ? `Application approved successfully. RFID: ${res.rfidNumber}`
            : 'Application approved successfully.';
          this.showApproveModal = false;
          this.selectedEligibility = null;
          this.fetchDetail(this.application!.applicationId);
          this.isSubmitting = false;
        },
        error: (err) => {
          this.actionError = err?.error?.message ?? 'Unable to approve application.';
          this.isSubmitting = false;
        },
      });
  }

  onShowRejectForm(): void {
    this.showRejectForm = true;
    this.showHardRejectForm = false;
    this.actionError = null;
    this.successMessage = null;
  }

  onShowHardRejectForm(): void {
    this.showHardRejectForm = true;
    this.showRejectForm = false;
    this.actionError = null;
    this.successMessage = null;
  }

  onCancelReject(): void {
    this.showRejectForm = false;
    this.rejectComment = '';
    this.actionError = null;
  }

  onCancelHardReject(): void {
    this.showHardRejectForm = false;
    this.rejectComment = '';
    this.actionError = null;
  }

  onReject(): void {
    if (!this.application?.applicationId || this.isSubmitting) return;

    const comment = this.rejectComment.trim();
    if (!comment) {
      this.actionError = 'Return comment is required.';
      return;
    }

    this.isSubmitting = true;
    this.actionError = null;
    this.successMessage = null;

    this.seniorCenterService.reviewApplication(this.application.applicationId, {
      status: 'returned',
      comment,
    }).subscribe({
      next: () => {
        this.successMessage = 'Application returned successfully.';
        this.showRejectForm = false;
        this.rejectComment = '';
        this.fetchDetail(this.application!.applicationId);
        this.isSubmitting = false;
      },
      error: (err) => {
        this.actionError = err?.error?.message ?? 'Unable to return application.';
        this.isSubmitting = false;
      },
    });
  }

  onHardReject(): void {
    if (!this.application?.applicationId || this.isSubmitting) return;

    const comment = this.rejectComment.trim();
    if (!comment) {
      this.actionError = 'Rejection comment is required.';
      return;
    }

    this.isSubmitting = true;
    this.actionError = null;
    this.successMessage = null;

    this.seniorCenterService.reviewApplication(this.application.applicationId, {
      status: 'rejected',
      comment,
    }).subscribe({
      next: () => {
        this.successMessage = 'Application rejected successfully.';
        this.showHardRejectForm = false;
        this.rejectComment = '';
        this.fetchDetail(this.application!.applicationId);
        this.isSubmitting = false;
      },
      error: (err) => {
        this.actionError = err?.error?.message ?? 'Unable to reject application.';
        this.isSubmitting = false;
      },
    });
  }

  onBackToList(): void {
    this.router.navigate(['/senior-center-applications']);
  }
}
